import { generateUID } from '@aytm/app/insights/dashboard/lib/strings'
import getBlockModelByType from '@aytm/app/insights/dashboard/models/blocks'
import { BlocksOrderedMap } from '@aytm/app/insights/dashboard/models/DashboardSchemaModel'

import { getBlock, getCurrentBlock, updateState } from './requests'
import { deleteBlock } from './schema'

import staticStore from '@aytm/app/insights/dashboard/stores/static.store'

export const copyOrCreateBlock = (state, { uid, patch }) => {
  const source = uid ? getBlock(state, uid) : getCurrentBlock(state)
  if (source) {
    const type = source.get('type')
    return getBlockModelByType(type)?.of({
      ...source.toJS(),
      uid: generateUID(),
      purpose: 'view',
      createdAt: null,
      createdBy: null,
      updatedAt: null,
      updatedBy: null,
      ...patch
    })
  } else {
    const type = staticStore.dashboardType
    return getBlockModelByType(type)?.of({
      uid: generateUID(),
      type,
      purpose: 'portal',
      ...patch
    })
  }
}

const insertBlock = (blocks, { block, uid, position = 1 }) => {
  if (!block) {
    return blocks
  }
  let newBlocks = blocks || BlocksOrderedMap()
  const blockUID = block.get('uid')
  if (newBlocks.has(uid)) {
    // Insert block before/after block with provided 'uid'
    // depending on 'position' property
    newBlocks = newBlocks.reduce((acc, cur) => {
      let result = acc
      const curUid = cur.get('uid')
      if (uid === curUid) {
        if (0 < position) {
          // Inserts new block after given block
          result = result.set(curUid, cur)
          result = result.set(blockUID, block)
        } else if (0 > position) {
          // Inserts new block before given block
          result = result.set(curUid, cur)
          result = result.set(blockUID, block)
        } else {
          // Replace block with given 'uid' with new one
          result = result.set(curUid, block)
        }
      } else {
        result = result.set(curUid, cur)
      }
      return result
    }, BlocksOrderedMap())
  } else {
    // If the block with given 'uid' is not found
    newBlocks =
      0 <= position
        ? // Add to the bottom
          newBlocks.set(blockUID, block)
        : // Add to the top
          newBlocks.reduce((acc, cur) => acc.set(cur.get('uid'), cur), BlocksOrderedMap().set(blockUID, block))
  }
  return newBlocks
}

export const addBlock = (state, { block, uid, position = 1, revertUid = null }) => {
  let newCurrentBlocks = insertBlock(state.currentSchema?.get('blocks'), {
    block: block?.clone(),
    uid,
    position
  })
  const newRemoteBlocks = insertBlock(state.remoteSchema?.get('blocks'), {
    block: block?.clone(),
    uid,
    position
  })
  if (revertUid) {
    const currentBlock = state.currentSchema?.getIn(['blocks', revertUid])
    const remoteBlock = state.remoteSchema?.getIn(['blocks', revertUid])
    if (currentBlock && remoteBlock) {
      newCurrentBlocks = newCurrentBlocks.set('uid', remoteBlock.clone())
    }
  }
  return [newCurrentBlocks, newRemoteBlocks]
}

export const replaceView = (state, { source, target, views, append }) => {
  const blocks = state.currentSchema.get('blocks')

  const sourceIdx = views.indexOf(source)
  const initialTargetIdx = views.indexOf(target)
  const idsWithoutSource = views.filter(id => id !== source)
  const targetIdx = idsWithoutSource.indexOf(target)
  const shouldCorrectForMoveDown = append || sourceIdx <= initialTargetIdx
  const correctedTargetIdx = shouldCorrectForMoveDown ? targetIdx + 1 : targetIdx

  const recomposedIds = idsWithoutSource
    .slice(0, correctedTargetIdx)
    .concat([source])
    .concat(idsWithoutSource.slice(correctedTargetIdx))

  const nextBlocksSource = new BlocksOrderedMap(recomposedIds.map(id => blocks.get(id)))

  const nextBlocks = append
    ? nextBlocksSource
    : nextBlocksSource
        .setIn([source, 'purpose'], blocks.get(target)?.get('purpose'))
        .setIn([target, 'purpose'], blocks.get(source)?.get('purpose'))

  const nextSchema = state.currentSchema.set('blocks', nextBlocks)
  return {
    ...state,
    currentSchema: nextSchema
  }
}

export const deleteView = (state, { uid }) => {
  const newCurrentSchema = deleteBlock(state.currentSchema, uid)
  const newRemoteSchema = deleteBlock(state.remoteSchema, uid)
  return updateState(state, {
    currentSchema: newCurrentSchema,
    remoteSchema: newRemoteSchema
  })
}
